import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function ParticipatingWholeLifeInsurance() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="participating-whole-life-insurance" />
          {/* Header */}
          <div className="text-center mb-12 pt-4"> {/* Added padding top */}
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Participating Whole Life Insurance</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Guaranteed Lifelong Protection with Potential Dividend Growth
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is Participating Whole Life? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is Participating Whole Life Insurance?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                Participating Whole Life insurance is a type of whole life policy that offers the same core guarantees (lifelong coverage, level premiums, guaranteed cash value) but adds the potential for policyholders to receive dividends from the insurance company.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                These dividends, while not guaranteed, are paid based on the insurer's financial performance (investment returns, mortality experience, expenses). When paid, dividends can significantly enhance the policy's cash value and death benefit over time, making it a powerful tool for long-term, tax-advantaged wealth accumulation and estate enhancement.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How It Works</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Policy Purchase & Premiums:</strong> Similar to standard whole life, you purchase a policy and pay guaranteed level premiums.
                </li>
                <li>
                  <strong>Guaranteed Growth:</strong> The policy includes guaranteed death benefit and cash value components.
                </li>
                <li>
                  <strong>Dividend Eligibility:</strong> As a policyholder, you are eligible to "participate" in the insurer's profits through potential dividends.
                </li>
                <li>
                  <strong>Dividend Declaration:</strong> The insurance company's board annually determines if dividends will be paid and the amount, based on performance. Dividends are NOT guaranteed.
                </li>
                <li>
                  <strong>Dividend Options:</strong> If dividends are paid, you typically choose how to receive them:
                    <ul className="list-disc list-inside ml-6 mt-1 text-sm">
                      <li><strong>Cash:</strong> Receive the dividend as a direct payment.</li>
                      <li><strong>Premium Reduction:</strong> Use the dividend to lower your out-of-pocket premium cost.</li>
                      <li><strong>Paid-Up Additions (PUAs):</strong> Purchase small, fully paid-up blocks of additional life insurance, increasing both your death benefit and cash value (most common and powerful option for growth).</li>
                      <li><strong>Accumulate at Interest:</strong> Leave dividends with the insurer to earn interest (interest earned is taxable).</li>
                    </ul>
                </li>
                 <li>
                  <strong>Enhanced Growth:</strong> Over time, especially when using PUAs, dividends can substantially increase the policy's total cash value and death benefit beyond the initial guarantees.
                 </li>
                 <li>
                  <strong>Access & Payout:</strong> Cash value (guaranteed portion + value from dividends/PUAs) can be accessed via loans. The total death benefit (guaranteed + PUA death benefits) is paid tax-free upon death.
                 </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>All Benefits of Whole Life:</strong> Includes lifelong coverage and guarantees.</li>
                <li><strong>Potential for Enhanced Growth:</strong> Dividends (especially via PUAs) can significantly boost cash value and death benefit over the long term.</li>
                <li><strong>Tax-Advantaged Accumulation:</strong> Both guaranteed cash value growth and growth from dividends (within the policy) are tax-deferred.</li>
                <li><strong>Flexibility via Dividend Options:</strong> Choose how dividends work best for your financial goals.</li>
                <li><strong>Powerful Estate Planning Tool:</strong> Creates a potentially larger, tax-free legacy compared to non-participating policies.</li>
                <li><strong>Inflation Hedge Potential:</strong> Dividend growth can help the policy's value keep pace with or exceed inflation over time.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Important factors to evaluate:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Dividends Not Guaranteed:</strong> Dividend payments depend on the insurer's performance and are not guaranteed. Illustrations show projected, non-guaranteed values.</li>
                <li><strong>Higher Premiums:</strong> Typically have higher premiums than non-participating whole life due to the potential for dividends.</li>
                <li><strong>Complexity:</strong> Understanding dividend scales and options requires careful explanation.</li>
                <li><strong>Long-Term Focus:</strong> The benefits of participation compound significantly over decades; it's a long-term strategy.</li>
                <li><strong>Insurer Performance Matters:</strong> The strength and consistency of the insurance company's dividend history is an important factor.</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is Participating Whole Life Right for You?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">This type of policy is often ideal for:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Individuals and business owners seeking long-term, tax-advantaged growth potential alongside guaranteed protection.</li>
                <li>Those focused on maximizing estate value and leaving a substantial legacy.</li>
                <li>People comfortable with non-guaranteed growth potential based on insurer performance.</li>
                <li>High-net-worth individuals looking for stable, alternative asset classes within a corporate structure or personally.</li>
                <li>Funding long-term goals like supplemental retirement income (via policy loans) or significant charitable gifts.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Combine Guarantees with Growth Potential</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Discover how Participating Whole Life insurance can provide a foundation of security while offering the potential to significantly enhance your wealth and legacy over time.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Explore Participating WL
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget /> // Removed onClose prop
      )}
    </MarketingLayout>
  );
}
